import React, { useState, useEffect, useCallback } from 'react';
import { Camera, MapPin, Clock, CheckCircle, XCircle, User, LogIn, LogOut, Wifi, WifiOff, AlertTriangle, Navigation } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { getDeviceId, getUserAgent } from './deviceId';
import { getFechaColombia } from './colombiaTime';

export default function AppMarcaje() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('scan');
  const [qrToken, setQrToken] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employees, setEmployees] = useState([]);
  const [marcajeType, setMarcajeType] = useState('entrada');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastMarcajes, setLastMarcajes] = useState([]);
  const [gpsStatus, setGpsStatus] = useState('unknown');

  const API_URL = process.env.REACT_APP_API_URL || 'https://asistencia-backend.emarodri834.workers.dev/api';

  // Detectar token del QR en URL y parámetro success
  useEffect(() => {
    const token = searchParams.get('token');
    const successFlag = searchParams.get('success');
    const empleado = searchParams.get('emp');
    const tipo = searchParams.get('tipo');
    const hora = searchParams.get('hora');
    
    if (successFlag === 'true' && empleado) {
      // Mostrar pantalla de éxito
      setSelectedEmployee(decodeURIComponent(empleado));
      setMarcajeType(tipo || 'entrada');
      setResult({
        success: true,
        mensaje: 'Marcaje registrado exitosamente',
        hora: hora ? decodeURIComponent(hora) : 'Registrada',
        distancia: 'OK'
      });
      setStep('result');
    } else if (token) {
      setQrToken(token);
      setStep('select');
    }
  }, [searchParams]);

  // Detectar conexión
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Verificar permisos de GPS al cargar
  useEffect(() => {
    const checkGPSPermission = async () => {
      if (!navigator.permissions) {
        setGpsStatus('unknown');
        return;
      }

      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setGpsStatus(result.state);
        
        result.addEventListener('change', () => {
          setGpsStatus(result.state);
        });
      } catch (error) {
        console.log('No se pudo verificar permisos GPS:', error);
        setGpsStatus('unknown');
      }
    };

    checkGPSPermission();
  }, []);

  // Cargar empleados
  const fetchEmployees = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/empleados?t=${Date.now()}`, {
        cache: 'no-cache'
      });
      const data = await response.json();
      console.log(`✅ Empleados activos cargados: ${data.empleados?.length || 0}`);
      setEmployees(data.empleados || []);
    } catch (error) {
      console.error('Error cargando empleados:', error);
    }
  }, [API_URL]);
  
  useEffect(() => {
    if (step === 'select') {
      console.log('🔄 Cargando empleados activos...');
      fetchEmployees();
    }
  }, [step, fetchEmployees]);
  
  // Obtener ubicación GPS con mejor manejo de errores
  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Tu dispositivo no tiene GPS'));
        return;
      }

      setGpsStatus('checking');

      const timeoutId = setTimeout(() => {
        reject(new Error('El GPS tardó mucho en responder. Verifica que esté activado.'));
      }, 15000);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          setGpsStatus('granted');
          resolve({
            latitud: position.coords.latitude,
            longitud: position.coords.longitude,
            precision: position.coords.accuracy
          });
        },
        (error) => {
          clearTimeout(timeoutId);
          let errorMsg = 'Error obteniendo ubicación';
          
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMsg = 'Debes activar el permiso de ubicación en tu navegador';
              setGpsStatus('denied');
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = 'No se pudo obtener tu ubicación. Verifica que el GPS esté activado';
              break;
            case error.TIMEOUT:
              errorMsg = 'El GPS tardó mucho. Verifica que esté activado y tengas buena señal';
              break;
            default:
              errorMsg = error.message || 'Error desconocido con el GPS';
          }
          
          reject(new Error(errorMsg));
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        }
      );
    });
  };

  // Solicitar permiso de GPS proactivamente
  const requestGPSPermission = async () => {
    try {
      setGpsStatus('checking');
      await getLocation();
      return true;
    } catch (error) {
      console.error('Error solicitando GPS:', error);
      return false;
    }
  };

  const loadEmployeeMarcajes = async (nombre) => {
    try {
      const response = await fetch(`${API_URL}/marcajes/empleado/${encodeURIComponent(nombre)}`);
      const data = await response.json();
      setLastMarcajes(data.slice(0, 3));
      
      if (data.length > 0) {
        const lastType = data[0].tipo;
        setMarcajeType(lastType === 'entrada' ? 'salida' : 'entrada');
      }
    } catch (error) {
      console.error('Error cargando marcajes:', error);
    }
  };

  const handleSelectEmployee = async (nombre) => {
    setSelectedEmployee(nombre);
    loadEmployeeMarcajes(nombre);
    
    console.log('📍 Solicitando permiso de GPS...');
    await requestGPSPermission();
    
    setStep('confirm');
  };

  const handleConfirmMarcaje = async () => {
    console.log('🚀 Iniciando marcaje...');
    setLoading(true);
    setResult(null);
  
    try {
      console.log('📍 Obteniendo ubicación GPS...');
      const loc = await getLocation();
      console.log('✅ Ubicación obtenida:', loc);
    
      const payload = {
        token: qrToken,
        empleado_nombre: selectedEmployee,
        tipo: marcajeType,
        latitud: loc.latitud,
        longitud: loc.longitud,
        device_id: getDeviceId(),
        user_agent: getUserAgent(),
        dispositivo: 'Web App'
      };

      console.log('📤 Enviando marcaje al servidor...');

      const response = await fetch(`${API_URL}/marcar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log('📥 Respuesta del servidor:', response.status);
      const data = await response.json();
      console.log('📦 Datos recibidos:', data);

      if (response.ok) {
        console.log('✅ Marcaje exitoso');
        const resultadoExito = {
          success: true,
          mensaje: data.mensaje || 'Marcaje registrado exitosamente',
          hora: data.hora || '',
          distancia: data.distancia || '0m'
        };
        setResult(resultadoExito);
        setLoading(false);
        setStep('result');
        
        // Forzar reload después de 2 segundos para garantizar que se vea el mensaje
        setTimeout(() => {
          window.location.href = window.location.href.split('?')[0] + 
            '?success=true&emp=' + encodeURIComponent(selectedEmployee) + 
            '&tipo=' + marcajeType +
            '&hora=' + encodeURIComponent(data.hora || 'Registrada');
        }, 2000);
      } else {
        console.log('❌ Error en marcaje:', data);
        const resultadoError = {
          success: false,
          error: data.error || 'Error desconocido',
          errorTipo: data.tipo || 'general'
        };
        setResult(resultadoError);
        setLoading(false);
        setStep('result');
      }
    } catch (error) {
      console.error('❌ Error crítico en marcaje:', error);
      
      const resultadoExcepcion = {
        success: false,
        error: error.message || 'Error de conexión',
        errorTipo: 'conexion'
      };
      setResult(resultadoExcepcion);
      setLoading(false);
      setStep('result');
    }
  };

  const handleReset = () => {
    console.log('🔄 Reiniciando proceso de marcaje...');
    setStep('scan');
    setQrToken('');
    setSelectedEmployee('');
    setResult(null);
    setLastMarcajes([]);
    setGpsStatus('unknown');
  };

  // Log para debugging
  useEffect(() => {
    console.log('📊 Estado actual:', { step, result, loading, gpsStatus });
  }, [step, result, loading, gpsStatus]);

  // Componente de resultado con error handling
  const ResultScreen = () => {
    try {
      if (!result) {
        return (
          <div className="text-center p-8">
            <Clock className="w-12 h-12 mx-auto mb-4 animate-spin" />
            <p>Procesando...</p>
          </div>
        );
      }

      return (
        <div className="text-center">
          {result.success ? (
            <>
              <div className="w-24 h-24 mx-auto mb-6 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                <CheckCircle className="w-16 h-16" />
              </div>
              <h2 className="text-3xl font-bold mb-4">✅ {selectedEmployee || 'Usuario'}</h2>
              <p className="text-2xl font-bold text-green-400 mb-8">
                {marcajeType ? marcajeType.toUpperCase() : 'MARCAJE'} registrada
              </p>
              
              <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-4 mb-6">
                <div className="text-lg mb-2">🕐 Hora: {result.hora || 'Registrada'}</div>
                <div className="text-sm opacity-80">📍 Distancia: {result.distancia || 'OK'}</div>
              </div>
            </>
          ) : (
            <>
              <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${
                result.errorTipo === 'duplicado' ? 'bg-yellow-500' : 'bg-red-500'
              }`}>
                {result.errorTipo === 'duplicado' ? (
                  <AlertTriangle className="w-16 h-16" />
                ) : (
                  <XCircle className="w-16 h-16" />
                )}
              </div>
              <h2 className={`text-3xl font-bold mb-4 ${
                result.errorTipo === 'duplicado' ? 'text-yellow-400' : 'text-red-400'
              }`}>
                {result.errorTipo === 'duplicado' ? '⚠️ Ya marcaste' : 'Error'}
              </h2>
              <p className="text-lg mb-6">{result.error || 'Error desconocido'}</p>
              
              {result.errorTipo === 'sin_entrada' && (
                <div className="bg-blue-500/20 border border-blue-500 rounded-lg p-4 mb-4">
                  <p className="text-sm">
                    💡 Debes marcar tu entrada antes de poder marcar la salida
                  </p>
                </div>
              )}

              {gpsStatus === 'denied' && (
                <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 mb-4">
                  <p className="text-sm font-semibold mb-2">📍 Cómo activar el GPS:</p>
                  <ol className="text-xs text-left space-y-1">
                    <li>1. Toca el ícono 🔒 en la barra de dirección</li>
                    <li>2. Busca "Ubicación" o "Location"</li>
                    <li>3. Selecciona "Permitir"</li>
                    <li>4. Recarga la página</li>
                  </ol>
                </div>
              )}
            </>
          )}

          <button
            onClick={handleReset}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105"
          >
            {result.success ? 'Marcar nuevamente' : 'Intentar de nuevo'}
          </button>
        </div>
      );
    } catch (error) {
      console.error('Error renderizando resultado:', error);
      return (
        <div className="text-center p-8">
          <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h3 className="text-xl font-bold mb-4">Error inesperado</h3>
          <p className="mb-6">{error.message}</p>
          <button
            onClick={handleReset}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-xl"
          >
            Volver a intentar
          </button>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 text-white">
      <div className="max-w-md mx-auto p-6">
        <div className="text-center mb-8 pt-8">
          <h1 className="text-3xl font-bold mb-2">🍽️ Marcaje de Asistencia</h1>
          <p className="text-lg opacity-80">Enruanados Gourmet</p>
          
          <div className="flex items-center justify-center gap-4 mt-4">
            <div className="flex items-center gap-2">
              {isOnline ? (
                <>
                  <Wifi className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-green-400">En línea</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-5 h-5 text-red-400" />
                  <span className="text-sm text-red-400">Sin conexión</span>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              {gpsStatus === 'granted' && (
                <>
                  <Navigation className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-green-400">GPS OK</span>
                </>
              )}
              {gpsStatus === 'checking' && (
                <>
                  <Navigation className="w-5 h-5 text-yellow-400 animate-pulse" />
                  <span className="text-sm text-yellow-400">Verificando GPS...</span>
                </>
              )}
              {gpsStatus === 'denied' && (
                <>
                  <Navigation className="w-5 h-5 text-red-400" />
                  <span className="text-sm text-red-400">GPS bloqueado</span>
                </>
              )}
            </div>
          </div>
        </div>

        {step === 'scan' && (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
            <div className="text-center mb-8">
              <Camera className="w-20 h-20 mx-auto mb-4 text-blue-400" />
              <h2 className="text-2xl font-bold mb-2">Escanea el código QR</h2>
              <p className="opacity-80">Se abrirá automáticamente al escanear</p>
            </div>
          </div>
        )}

        {step === 'select' && (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
            <div className="text-center mb-6">
              <User className="w-16 h-16 mx-auto mb-4 text-green-400" />
              <h2 className="text-2xl font-bold mb-2">Selecciona tu nombre</h2>
              <p className="opacity-80">¿Quién está marcando?</p>
            </div>

            {gpsStatus === 'denied' && (
              <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <span className="font-semibold text-red-400">GPS bloqueado</span>
                </div>
                <p className="text-sm">
                  Debes activar el permiso de ubicación en tu navegador para poder marcar asistencia.
                </p>
              </div>
            )}

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {employees.map((emp, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSelectEmployee(emp)}
                  className="w-full bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-4 text-left transition-all transform hover:scale-105"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-2xl font-bold">
                      {emp[0]}
                    </div>
                    <div>
                      <div className="font-semibold text-lg">{emp}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <button
              onClick={handleReset}
              className="w-full mt-6 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 text-white font-bold py-3 px-6 rounded-xl transition-all"
            >
              Cancelar
            </button>
          </div>
        )}

        {step === 'confirm' && (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
            <div className="text-center mb-6">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center">
                <User className="w-12 h-12" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{selectedEmployee}</h2>
              <p className="opacity-80">Confirma tu marcaje</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm opacity-80 mb-3">Tipo de marcaje:</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setMarcajeType('entrada')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    marcajeType === 'entrada'
                      ? 'bg-green-500/30 border-green-500'
                      : 'bg-white/10 border-white/20'
                  }`}
                >
                  <LogIn className="w-8 h-8 mx-auto mb-2" />
                  <div className="font-bold">Entrada</div>
                </button>
                <button
                  onClick={() => setMarcajeType('salida')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    marcajeType === 'salida'
                      ? 'bg-blue-500/30 border-blue-500'
                      : 'bg-white/10 border-white/20'
                  }`}
                >
                  <LogOut className="w-8 h-8 mx-auto mb-2" />
                  <div className="font-bold">Salida</div>
                </button>
              </div>
            </div>

            {lastMarcajes.length > 0 && (
              <div className="mb-6 p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="text-sm opacity-80 mb-2">
                  {marcajeType === 'salida' ? 'Tu entrada de hoy:' : 'Últimos marcajes:'}
                </div>
                {marcajeType === 'salida' ? (
                  lastMarcajes
                    .filter(m => m.tipo === 'entrada' && m.fecha === getFechaColombia())
                    .slice(0, 1)
                    .map((m, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-green-500/20 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          <span className="text-green-400 font-semibold">Entrada registrada</span>
                        </div>
                        <span className="text-white font-bold text-lg">{m.hora}</span>
                      </div>
                    ))
                ) : (
                  lastMarcajes.map((m, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1">
                      <span>{m.fecha}</span>
                      <span className={m.tipo === 'entrada' ? 'text-green-400' : 'text-blue-400'}>
                        {m.tipo} - {m.hora}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}

            <button
              onClick={handleConfirmMarcaje}
              disabled={loading}
              className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 disabled:opacity-50 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Clock className="w-6 h-6 animate-spin" />
                  {gpsStatus === 'checking' ? 'Obteniendo GPS...' : 'Validando...'}
                </>
              ) : (
                <>
                  <CheckCircle className="w-6 h-6" />
                  Confirmar {marcajeType}
                </>
              )}
            </button>

            <button
              onClick={() => setStep('select')}
              disabled={loading}
              className="w-full mt-4 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold py-3 px-6 rounded-xl transition-all disabled:opacity-50"
            >
              Volver
            </button>
          </div>
        )}

        {step === 'result' && (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
            <ResultScreen />
          </div>
        )}

        <div className="mt-6 text-center text-sm opacity-60">
          <MapPin className="w-4 h-4 inline mr-1" />
          Debes estar dentro del restaurante para marcar
        </div>
      </div>
    </div>
  );
}
